import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["id", "children", "content", "direction", "view", "opened", "onClick", "onKeyDown", "classes", "interactive", "disableHover", "popoverProps", "offset", "arrowOffset", "possibleDirections", "title", "subTitle", "actions", "onClose", "titleProps", "subTitleProps", "contentTextProps", "getContainer", "isHideWhenOutOfBlock"];

var _textColorsMap;

import React, { forwardRef, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { CloseLinear } from "../icons";
import { ButtonBase } from "../internal/ButtonBase";
import { ClickOutside } from "../internal/ClickOutside";
import { Portal } from "../internal/Portal";
import { defaultPossibleDirections } from "../internal/Portal/helpers";
import { TextBase } from "../internal/TextBase";
import { keyCode } from "../utils/keyCodes";
import noop from "../utils/noop";
import { isKeyboardEvent, views } from './constants';
import styles from './OnBoardingTooltip.css';
var cx = classNames.bind(styles);
var textColorsMap = (_textColorsMap = {}, _defineProperty(_textColorsMap, views.dark, 'W100'), _defineProperty(_textColorsMap, views.white, 'N900'), _textColorsMap);
export var OnBoardingTooltip = /*#__PURE__*/forwardRef(function OnBoardingTooltip(_ref, ref) {
  var id = _ref.id,
      children = _ref.children,
      content = _ref.content,
      _ref$direction = _ref.direction,
      initDirection = _ref$direction === void 0 ? 'top' : _ref$direction,
      _ref$view = _ref.view,
      view = _ref$view === void 0 ? 'white' : _ref$view,
      opened = _ref.opened,
      onClick = _ref.onClick,
      _ref$onKeyDown = _ref.onKeyDown,
      onKeyDown = _ref$onKeyDown === void 0 ? noop : _ref$onKeyDown,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      _ref$interactive = _ref.interactive,
      interactive = _ref$interactive === void 0 ? true : _ref$interactive,
      disableHover = _ref.disableHover,
      _ref$popoverProps = _ref.popoverProps,
      popoverProps = _ref$popoverProps === void 0 ? defaultPropObject : _ref$popoverProps,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 16 : _ref$offset,
      _ref$arrowOffset = _ref.arrowOffset,
      arrowOffset = _ref$arrowOffset === void 0 ? 16 : _ref$arrowOffset,
      _ref$possibleDirectio = _ref.possibleDirections,
      possibleDirections = _ref$possibleDirectio === void 0 ? defaultPossibleDirections : _ref$possibleDirectio,
      title = _ref.title,
      subTitle = _ref.subTitle,
      actions = _ref.actions,
      onClose = _ref.onClose,
      _ref$titleProps = _ref.titleProps,
      titleProps = _ref$titleProps === void 0 ? defaultPropObject : _ref$titleProps,
      _ref$subTitleProps = _ref.subTitleProps,
      subTitleProps = _ref$subTitleProps === void 0 ? defaultPropObject : _ref$subTitleProps,
      _ref$contentTextProps = _ref.contentTextProps,
      contentTextProps = _ref$contentTextProps === void 0 ? defaultPropObject : _ref$contentTextProps,
      getContainer = _ref.getContainer,
      isHideWhenOutOfBlock = _ref.isHideWhenOutOfBlock,
      props = _objectWithoutProperties(_ref, _excluded);

  var childrenRef = useRef(null);
  var contentRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hover = _useState2[0],
      setHover = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      direction = _useState4[0],
      setDirection = _useState4[1];

  var handleMouseEnter = function handleMouseEnter() {
    setHover(true);
  };

  var handleMouseLeave = function handleMouseLeave() {
    setHover(false);
  };

  var handleKeyDown = function handleKeyDown(e) {
    if (isKeyboardEvent(e)) {
      onKeyDown(e);

      if (e.keyCode === keyCode.SPACE && onClick && !isKeyboardEvent(e)) {
        onClick(e);
      }
    }
  };

  var onSetDirection = function onSetDirection(newDirection) {
    if (newDirection) {
      setDirection(newDirection);
    }
  };

  var hasTitle = Boolean(title || subTitle);
  var hasCloseButton = Boolean(onClose);
  var accessibilityProps = {
    role: 'button',
    className: cx('root', classes.root),
    onKeyDown: handleKeyDown,
    tabIndex: 0,
    onClick: onClick
  };
  var ignoreRefs = useMemo(function () {
    return [childrenRef, contentRef];
  }, []);
  var container = typeof getContainer === 'function' ? getContainer() : undefined;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({}, props, {
    ref: childrenRef,
    onMouseEnter: disableHover ? undefined : handleMouseEnter,
    onMouseLeave: disableHover ? undefined : handleMouseLeave,
    "aria-labelledby": id
  }, onClick ? accessibilityProps : undefined), children), (opened || hover) && /*#__PURE__*/React.createElement(ClickOutside, {
    onClickOutside: opened ? onClick || onClose : noop,
    ignoreRefs: ignoreRefs
  }, /*#__PURE__*/React.createElement(Portal, _extends({}, popoverProps, {
    role: "tooltip",
    id: id,
    container: container,
    ref: ref,
    interactive: interactive,
    anchorRef: childrenRef,
    direction: initDirection,
    possibleDirections: possibleDirections,
    onSetDirection: onSetDirection,
    offset: offset,
    arrowOffset: arrowOffset,
    className: cx('tooltip', "view_".concat(view), classes.tooltip),
    isHideWhenOutOfBlock: isHideWhenOutOfBlock
  }), /*#__PURE__*/React.createElement("div", {
    ref: contentRef,
    className: cx('content', {
      has_close_button: hasCloseButton
    }, classes.content)
  }, hasCloseButton && /*#__PURE__*/React.createElement(ButtonBase, {
    className: cx('close', classes.close),
    onClick: onClose
  }, /*#__PURE__*/React.createElement(CloseLinear, null)), /*#__PURE__*/React.createElement("div", {
    className: cx('inner', classes.inner)
  }, hasTitle && /*#__PURE__*/React.createElement("div", {
    className: cx('content_top')
  }, /*#__PURE__*/React.createElement(TextBase, _extends({}, titleProps, {
    as: "h4",
    display: "inline",
    weight: "semibold",
    color: textColorsMap[view]
  }), title), ' ', /*#__PURE__*/React.createElement(TextBase, _extends({}, subTitleProps, {
    as: "span",
    display: "inline",
    color: "N80"
  }), subTitle)), content && /*#__PURE__*/React.createElement(TextBase, _extends({}, contentTextProps, {
    color: textColorsMap[view],
    size: "m"
  }), content), actions && /*#__PURE__*/React.createElement("div", {
    className: cx('actions', classes.actions)
  }, actions))), /*#__PURE__*/React.createElement("div", {
    className: cx('arrow', "direction_".concat(direction), classes.arrow)
  }))));
});
export default OnBoardingTooltip;